/**
 * @fileoverview AR-924 — Step 2 of "Upload for class": shared date bar, File / Student / Status table,
 * notes and the confirm footer.
 */

import { Button, Group, Paper, Stack, Switch, Table, Text } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { dateInputFormat, fromIsoDay, toIsoDay } from '../deliverableDrawer/teacher/dateFormat'
import { MatchRow } from './MatchRow'
import { groupedWith, rejectedLocally, uploadable } from './stagedItems'
import type { UploadForClassState } from './useUploadForClass'

const DATE_WIDTH = 150
const CALENDAR_ICON_SIZE = 14
/** Figma: uppercase, small, gray column headers on a gray-0 band. */
const HEADER_LETTER_SPACING = 0.4

interface MatchStepProps {
  readonly state: UploadForClassState
  readonly studentOptions: { value: string; label: string }[]
  /** Students hidden from the selects because they already submitted themselves. */
  readonly excludedNames: readonly string[]
  readonly onCancel: () => void
  readonly onConfirm: () => void
}

export function MatchStep({ state, studentOptions, excludedNames, onCancel, onConfirm }: MatchStepProps) {
  const { t, i18n } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.upload_for_class.${key}`, options)
  const rows = uploadable(state.items)
  const failedNames = state.items.filter(rejectedLocally).map(item => item.name)
  const excludedNote = excludedNames.length > 0 ? tr('excluded_note', { names: excludedNames.join(', ') }) : ''
  const failedNote = failedNames.length > 0 ? tr('failed_note', { names: failedNames.join(', ') }) : ''
  const notes = [excludedNote, failedNote].filter(Boolean).join(' ')

  return (
    <Stack spacing="md">
      <Paper radius="md" px="md" py="xs" sx={theme => ({ background: theme.colors.gray[0] })}>
        <Group spacing="md" noWrap>
          <Switch
            checked={state.sameDate}
            onChange={event => state.setSameDate(event.currentTarget.checked)}
            disabled={state.submitting}
          />
          <Text size="sm" weight={600}>{tr('same_date_for_all')}</Text>
          <DateInput
            value={fromIsoDay(state.date)}
            onChange={date => state.setDate(date ? toIsoDay(date) : null)}
            valueFormat={dateInputFormat(i18n.language)}
            placeholder={tr('date_defaults_to_due')}
            icon={<IconCalendar size={CALENDAR_ICON_SIZE} />}
            size="xs"
            clearable={false}
            disabled={!state.sameDate || state.submitting}
            popoverProps={{ withinPortal: true }}
            sx={{ width: DATE_WIDTH }}
            aria-label={tr('same_date_for_all')}
          />
          <Text
            size="xs"
            color="dimmed"
            sx={theme => ({ borderLeft: `1px solid ${theme.colors.gray[3]}`, paddingLeft: 12 })}
          >
            {tr('auto_match_hint')}
          </Text>
        </Group>
      </Paper>

      <Paper withBorder radius="md">
        <Table
          verticalSpacing="sm"
          horizontalSpacing="sm"
          sx={theme => ({
            '& thead tr th': {
              textTransform: 'uppercase',
              fontSize: theme.fontSizes.xs,
              letterSpacing: HEADER_LETTER_SPACING,
              color: theme.colors.gray[6],
              backgroundColor: theme.colors.gray[0],
            },
          })}
        >
          <thead>
            <tr>
              <th>{tr('col_file')}</th>
              <th>{tr('col_student')}</th>
              <th>{tr('col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(item => (
              <MatchRow
                key={item.id}
                item={item}
                groupedWithName={groupedWith(item, state.items)?.name ?? null}
                studentOptions={studentOptions}
                disabled={state.submitting}
                onAssign={state.assign}
              />
            ))}
          </tbody>
        </Table>
      </Paper>

      {notes && <Text size="xs" color="dimmed">{notes}</Text>}

      <Group position="apart" pt="xs">
        <Text size="sm" color="dimmed">
          {tr('summary_matched', {
            matched: rows.length - state.unassigned, total: rows.length, students: state.studentsCount,
          })}
          {state.unassigned > 0 && (
            <Text span color="orange" weight={600}> · {tr('needs_student_count', { count: state.unassigned })}</Text>
          )}
        </Text>
        <Group spacing="xs">
          <Button variant="default" onClick={onCancel} disabled={state.submitting}>{tr('cancel')}</Button>
          <Button onClick={onConfirm} disabled={!state.canConfirm} loading={state.submitting}>
            {state.submitting ? tr('uploading_for') : tr('upload_for_students', { count: state.studentsCount })}
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}
